import { useParams } from '@reach/router';
import { formatISO, subDays } from 'date-fns';
import { graphql } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';

import {
  LimitPeriodDetailList,
  limitPeriodDetailList_periodLimit,
} from '@/bits';
import {
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  CardOptionsButton,
  CheckboxField,
  ControlledModal,
  DetailList,
  ErrorMessage,
  Form,
  InlineIconButton,
  Pill,
  SubmitButton,
  Value,
  useModalContext,
} from '@/components';
import { Bar } from '@/components/Bar';
import { EditIcon, InformationIcon, RefreshIcon } from '@/components/icons';
import { TranslateFn, useTranslate } from '@/contexts';
import { LimitType, LimitTypeEnum } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import formatDate from '@/utils/formatter/formatDate';
import formatMoney from '@/utils/formatter/formatMoney';
import {
  adjustmentSummary_stats,
  depositSummary_stats,
  formatCurrencyAmountRecord,
  getAdjustmentsSummaryRecord,
  getDepositsSummaryRecord,
} from '@/utils/stats-with-currency';
import {
  PlayerWalletBoxQuery,
  PlayerWalletBoxQueryVariables,
} from './__generated__/component';

export const Fragment = graphql`
  fragment SanityPlayerWalletBlock on SanityPlayerWalletBlock {
    title {
      ...SanityLocaleString
    }
    balanceLabel {
      ...SanityLocaleString
    }
    reservedBalanceLabel {
      ...SanityLocaleString
    }
    upcomingCashbackLabel {
      ...SanityLocaleString
    }
    depositLimitsLabel {
      ...SanityLocaleString
    }
    lossLimitsLabel {
      ...SanityLocaleString
    }
    withdrawalLimitsLabel {
      ...SanityLocaleString
    }
    last24hDepositsLabel {
      ...SanityLocaleString
    }
    last24hAdjustmentsLabel {
      ...SanityLocaleString
    }
    sowExpendingUpperBoundLabel {
      ...SanityLocaleString
    }
    dailyLabel {
      ...SanityLocaleString
    }
    weeklyLabel {
      ...SanityLocaleString
    }
    monthlyLabel {
      ...SanityLocaleString
    }
    resetAtLabel {
      ...SanityLocaleString
    }
  }
`;

const wageringRequirementFragment = gql`
  fragment PlayerWalletBlock_wageringRequirement on WageringRequirementSummary {
    canWithdraw
    cycleOpen
    isMet
    totalDeposited
    totalWagered
    remainingToWager
    remainingToWagerIfEnforced
    bypass
    freeSpinCycle {
      cycleOpen
      isMet
      totalDeposited
      totalWagered
      remainingToWager
      mode
    }
  }
`;

const QUERY = gql`
  query PlayerWalletBox(
    $playerId: ID!
    $last24hFrom: OffsetDateTime!
    $now: OffsetDateTime!
  ) {
    player(playerId: $playerId) {
      id
      upcomingCashbackV2 {
        amount
        currency
      }
      sourceOfWealth {
        expendingUpperBound
      }
      wallet {
        id
        currency
        balance
        reservedBalance
        withdrawalLimit {
          dayLimit {
            value
            available
            currentPeriodEndTime
            ...LimitPeriodDetailList_periodLimit
          }
          monthLimit {
            value
            available
            currentPeriodEndTime
            ...LimitPeriodDetailList_periodLimit
          }
        }
        depositLimit {
          dayLimit {
            value
            available
            currentPeriodEndTime
            ...LimitPeriodDetailList_periodLimit
          }
          weekLimit {
            value
            available
            currentPeriodEndTime
            ...LimitPeriodDetailList_periodLimit
          }
          monthLimit {
            value
            available
            currentPeriodEndTime
            ...LimitPeriodDetailList_periodLimit
          }
        }
        lossLimit {
          dayLimit {
            value
            available
            currentPeriodEndTime
            ...LimitPeriodDetailList_periodLimit
          }
          weekLimit {
            value
            available
            currentPeriodEndTime
            ...LimitPeriodDetailList_periodLimit
          }
          monthLimit {
            value
            available
            currentPeriodEndTime
            ...LimitPeriodDetailList_periodLimit
          }
        }
        wageringRequirement {
          ...PlayerWalletBlock_wageringRequirement
        }
      }
      last24hStats: stats(from: $last24hFrom, to: $now, granularity: All) {
        rows {
          ...AdjustmentSummary_stats
          ...DepositSummary_stats
        }
      }
    }
  }
  ${wageringRequirementFragment}
  ${limitPeriodDetailList_periodLimit}
  ${adjustmentSummary_stats}
  ${depositSummary_stats}
`;

type LimitPeriod =
  | NonNullable<
      NonNullable<
        PlayerWalletBoxQuery['player']['wallet']
      >['depositLimit']['dayLimit']
    >
  | NonNullable<
      NonNullable<
        PlayerWalletBoxQuery['player']['wallet']
      >['depositLimit']['weekLimit']
    >
  | NonNullable<
      NonNullable<
        PlayerWalletBoxQuery['player']['wallet']
      >['depositLimit']['monthLimit']
    >
  | NonNullable<
      NonNullable<
        PlayerWalletBoxQuery['player']['wallet']
      >['withdrawalLimit']['dayLimit']
    >
  | NonNullable<
      NonNullable<
        PlayerWalletBoxQuery['player']['wallet']
      >['withdrawalLimit']['monthLimit']
    >;

const SET_WAGERING_BYPASS_MUTATION = gql`
  mutation SetWageringRequirementBypass(
    $playerId: ID!
    $wageringRequirementBypass: Boolean!
  ) {
    setWageringRequirementBypass(
      playerId: $playerId
      wageringRequirementBypass: $wageringRequirementBypass
    ) {
      id
      wageringRequirement {
        ...PlayerWalletBlock_wageringRequirement
      }
    }
  }
  ${wageringRequirementFragment}
`;

const SetWageringBypassForm: FC<{
  playerId: string;
  currentBypass?: boolean;
  onSuccess?: () => void;
}> = ({ playerId, currentBypass, onSuccess }) => {
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();
  const { close } = useModalContext();

  const [{ fetching: submitting }, setBypass] = useMutation(
    SET_WAGERING_BYPASS_MUTATION,
  );

  const defaultValues = { bypass: currentBypass ?? false };

  const onSubmit = ({ bypass }: typeof defaultValues) => {
    setBypass({ playerId, wageringRequirementBypass: bypass }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else {
        onSuccess?.();

        if (close) {
          close();
        }
      }
    });
  };

  return (
    <Card
      size="sm"
      title="Set Wagering Requirement Bypass"
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3">
          <Form
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            className="grid gap-4"
          >
            <CheckboxField
              title="Bypass wagering requirement"
              name="bypass"
              id="SetWageringBypassForm_bypass"
            />
            <ErrorMessage message={errorMessage} />
            <SubmitButton value="Save" disabled={submitting} />
          </Form>
        </div>
      </CardBody>
    </Card>
  );
};

const CLOSE_WAGERING_CYCLE_MUTATION = gql`
  mutation CloseWageringRequirementCycle($playerId: ID!) {
    closeWageringRequirementCycle(playerId: $playerId) {
      id
      wageringRequirement {
        ...PlayerWalletBlock_wageringRequirement
      }
    }
  }
  ${wageringRequirementFragment}
`;

const CLOSE_FREE_SPIN_WAGERING_CYCLE_MUTATION = gql`
  mutation CloseFreeSpinWageringCycle($playerId: ID!) {
    closeFreeSpinWageringCycle(playerId: $playerId) {
      id
      wageringRequirement {
        ...PlayerWalletBlock_wageringRequirement
      }
    }
  }
  ${wageringRequirementFragment}
`;

const CloseWageringCycleForm: FC<{
  playerId: string;
  mutation: typeof CLOSE_WAGERING_CYCLE_MUTATION;
  title: string;
  message: string;
  onSuccess?: () => void;
}> = ({ playerId, mutation, title, message, onSuccess }) => {
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();
  const { close } = useModalContext();

  const [{ fetching: submitting }, closeCycle] = useMutation(mutation);

  const onSubmit = () => {
    closeCycle({ playerId }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else {
        onSuccess?.();

        if (close) {
          close();
        }
      }
    });
  };

  return (
    <Card
      size="sm"
      title={title}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3">
          <Form defaultValues={{}} onSubmit={onSubmit} className="grid gap-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {message}
            </p>
            <ErrorMessage message={errorMessage} />
            <SubmitButton value="Close cycle" disabled={submitting} />
          </Form>
        </div>
      </CardBody>
    </Card>
  );
};

const LimitInfo = ({
  limit,
  fetching,
  labelTranslation,
  currency,
  limitPeriodType,
  limitTypeTitle,
  resetAt,
  resetAtTranslation,
}: {
  limit: Nullable<LimitPeriod>;
  fetching: boolean;
  labelTranslation: Parameters<TranslateFn>[0];
  currency: Nullable<string>;
  limitTypeTitle: string;
  limitPeriodType: LimitTypeEnum;
  resetAt?: boolean;
  resetAtTranslation?: string;
}) => {
  const { t } = useTranslate();

  const currencyValueFormatter = useCallback(
    (value: Nullable<number>) => formatMoney(value, currency),
    [currency],
  );

  return (
    <div className="flex items-center justify-between">
      <Bar
        loading={fetching}
        value={
          ((limit?.value ?? 0) - (limit?.available ?? 0)) / (limit?.value ?? 0)
        }
      >
        {t(labelTranslation, {
          value: formatMoney((limit?.value ?? 0) - (limit?.available ?? 0)),
          of: formatMoney(limit?.value, currency),
        })}
        {resetAt &&
          ` - ${resetAtTranslation} ${formatDate(limit?.currentPeriodEndTime)}`}
      </Bar>
      <ControlledModal
        content={
          limit && (
            <LimitPeriodDetailList
              limit={limit}
              limitPeriodType={limitPeriodType}
              limitTypeTitle={limitTypeTitle}
              formatValue={currencyValueFormatter}
            />
          )
        }
      >
        <InlineIconButton disabled={!limit}>
          <InformationIcon />
        </InlineIconButton>
      </ControlledModal>
    </div>
  );
};

const WageringCycleBar = ({
  fetching,
  title,
  totalWagered,
  totalDeposited,
  remainingToWager,
  isMet,
  currency,
}: {
  fetching: boolean;
  title: string;
  totalWagered?: Nullable<number>;
  totalDeposited?: Nullable<number>;
  remainingToWager?: Nullable<number>;
  isMet?: Nullable<boolean>;
  currency: Nullable<string>;
}) => {
  const tw = totalWagered ?? 0;
  const total = totalDeposited ?? 0;
  const hasData = totalWagered != null || totalDeposited != null;
  const remaining = remainingToWager ?? Math.max(total - tw, 0);

  const wageredPercent = !hasData
    ? 0
    : isMet
      ? 100
      : total > 0
        ? Math.min(tw / total, 1) * 100
        : 0;

  const redPercent = !hasData
    ? 100
    : isMet || total > 0
      ? 100 - wageredPercent
      : 0;
  const hasColor = wageredPercent > 0 || redPercent > 0;

  return (
    <div className="flex items-center justify-between">
      <div
        className={`shadow w-full h-6 bg-gray-300 dark:bg-gray-700 leading-none text-sm relative overflow-hidden${fetching ? ' animate-pulse' : ''}`}
      >
        {!fetching && (
          <>
            {wageredPercent > 0 && (
              <div
                className="bg-green-500 h-full absolute top-0 left-0"
                style={{ width: `${wageredPercent}%` }}
              />
            )}
            {redPercent > 0 && (
              <div
                className="bg-red-500 h-full absolute top-0"
                style={{ left: `${wageredPercent}%`, width: `${redPercent}%` }}
              />
            )}
            <span
              className={`absolute inset-0 flex items-center px-1 text-sm ${hasColor ? 'text-white' : 'text-black dark:text-gray-200'}`}
            >
              {`${isMet ? 'Completed — ' : ''}${formatMoney(tw, currency)} wagered of ${formatMoney(total, currency)}`}
            </span>
          </>
        )}
      </div>
      <ControlledModal
        content={
          hasData ? (
            <Card
              size="lg"
              title={title}
              options={
                <CardOptions>
                  <CardCloseButton />
                </CardOptions>
              }
            >
              <DetailList
                items={[
                  {
                    label: 'Total Deposited',
                    value: formatMoney(totalDeposited, currency),
                  },
                  {
                    label: 'Total Wagered',
                    value: formatMoney(totalWagered, currency),
                  },
                  {
                    label: 'Remaining to Wager',
                    value: formatMoney(remaining, currency),
                  },
                ]}
              />
            </Card>
          ) : null
        }
      >
        <InlineIconButton disabled={!hasData}>
          <InformationIcon />
        </InlineIconButton>
      </ControlledModal>
    </div>
  );
};

const PlayerWalletBlock: FC<{
  block: Queries.SanityPlayerWalletBlockFragment;
}> = ({ block }) => {
  const { t } = useTranslate();
  const params = useParams();
  const isEstonia = process.env.GATSBY_LICENSE === 'EST';

  const dateVariables = useMemo<
    Pick<PlayerWalletBoxQueryVariables, 'last24hFrom' | 'now'>
  >(() => {
    const now = new Date();
    return { now: formatISO(now), last24hFrom: formatISO(subDays(now, 1)) };
  }, []);

  const [{ data, fetching }, refresh] = useQuery<
    PlayerWalletBoxQuery,
    PlayerWalletBoxQueryVariables
  >({
    query: QUERY,
    variables: {
      playerId: params.playerId,
      ...dateVariables,
    },
  });

  const last24hStats = data?.player.last24hStats?.rows;
  const sow = data?.player.sourceOfWealth;

  const last24hAdjustmentSummary = getAdjustmentsSummaryRecord(last24hStats);
  const last24hDepositsSummary = getDepositsSummaryRecord(last24hStats);

  const wageringRequirement = data?.player.wallet?.wageringRequirement;
  const freeSpinCycle = wageringRequirement?.freeSpinCycle;

  return (
    <Card
      size="md"
      title={t(block.title)}
      options={
        <CardOptions>
          <CardOptionsButton
            className="flex"
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          <Value
            fetching={fetching}
            title={t(block.balanceLabel)}
            value={formatMoney(
              data?.player?.wallet?.balance,
              data?.player?.wallet?.currency,
            )}
          />
          <Value
            fetching={fetching}
            title={t(block.reservedBalanceLabel)}
            value={formatMoney(
              data?.player?.wallet?.reservedBalance,
              data?.player?.wallet?.currency,
            )}
          />
          <Value
            fetching={fetching}
            title={t(block.upcomingCashbackLabel)}
            value={formatMoney(
              data?.player.upcomingCashbackV2?.amount,
              data?.player.upcomingCashbackV2?.currency,
            )}
          />
          <Value
            fetching={fetching}
            title={t(block.last24hDepositsLabel)}
            value={formatCurrencyAmountRecord(last24hDepositsSummary)}
          />
          <Value
            fetching={fetching}
            title={t(block.last24hAdjustmentsLabel)}
            value={formatCurrencyAmountRecord(last24hAdjustmentSummary)}
          />
          <Value
            fetching={fetching}
            title={t(block.sowExpendingUpperBoundLabel)}
            value={formatMoney(
              sow?.expendingUpperBound,
              data?.player.wallet?.currency,
            )}
          />
          <div className="col-span-full flex items-center gap-2 my-2">
            <span className="text-sm text-gray-500 font-semibold whitespace-nowrap">
              Wagering Requirements
            </span>
            <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
          </div>
          <Value
            fetching={fetching}
            title="Remaining to Wager"
            value={formatMoney(
              wageringRequirement?.remainingToWager,
              data?.player.wallet?.currency,
            )}
          />
          {freeSpinCycle?.mode === 'track' && (
            <Value
              fetching={fetching}
              title="Remaining to Wager (if enforced)"
              value={formatMoney(
                wageringRequirement?.remainingToWagerIfEnforced,
                data?.player.wallet?.currency,
              )}
            />
          )}
          <Value
            fetching={fetching}
            title="Can Withdraw"
            value={
              data?.player.wallet?.wageringRequirement != null
                ? data.player.wallet.wageringRequirement.canWithdraw
                  ? 'Yes'
                  : 'No'
                : undefined
            }
          />
          <Value
            fetching={fetching}
            title="Is Bypassed"
            value={
              data?.player.wallet?.wageringRequirement != null
                ? data.player.wallet.wageringRequirement.bypass
                  ? 'Yes'
                  : 'No'
                : undefined
            }
            suffix={
              <ControlledModal
                content={
                  data?.player.id ? (
                    <SetWageringBypassForm
                      playerId={data.player.id}
                      currentBypass={
                        data.player.wallet?.wageringRequirement?.bypass ?? false
                      }
                      onSuccess={() =>
                        refresh({ requestPolicy: 'network-only' })
                      }
                    />
                  ) : null
                }
              >
                <InlineIconButton>
                  <EditIcon />
                </InlineIconButton>
              </ControlledModal>
            }
          />
          <Value
            fetching={fetching}
            title="Cycle Open"
            value={
              data?.player.wallet?.wageringRequirement != null
                ? data.player.wallet.wageringRequirement.cycleOpen
                  ? 'Yes'
                  : 'No'
                : undefined
            }
            suffix={
              <ControlledModal
                content={
                  data?.player.id ? (
                    <CloseWageringCycleForm
                      playerId={data.player.id}
                      mutation={CLOSE_WAGERING_CYCLE_MUTATION}
                      title="Close Wagering Cycle"
                      message="Are you sure you want to close the wagering requirement cycle?"
                      onSuccess={() =>
                        refresh({ requestPolicy: 'network-only' })
                      }
                    />
                  ) : null
                }
              >
                <InlineIconButton>
                  <EditIcon />
                </InlineIconButton>
              </ControlledModal>
            }
          />
          <Value
            fetching={fetching}
            title="Requirement Met"
            value={
              data?.player.wallet?.wageringRequirement != null
                ? data.player.wallet.wageringRequirement.isMet
                  ? 'Yes'
                  : 'No'
                : undefined
            }
          />
          <Value
            fetching={fetching}
            title="Total Deposited"
            value={formatMoney(
              data?.player.wallet?.wageringRequirement?.totalDeposited,
              data?.player.wallet?.currency,
            )}
          />
          <div className="col-span-full">
            <label className="text-sm text-gray-500 font-semibold">
              Wagering — all games
            </label>
            <div className="grid gap-2 pt-1">
              <WageringCycleBar
                fetching={fetching}
                title="Wagering — all games"
                totalWagered={wageringRequirement?.totalWagered}
                totalDeposited={wageringRequirement?.totalDeposited}
                isMet={wageringRequirement?.isMet}
                currency={data?.player.wallet?.currency}
              />
            </div>
          </div>
          {freeSpinCycle?.cycleOpen && (
            <div className="col-span-full">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500 font-semibold">
                  Free spins (slots & crash)
                </label>
                <Pill>{freeSpinCycle.mode.toUpperCase()}</Pill>
                <ControlledModal
                  content={
                    data?.player.id ? (
                      <CloseWageringCycleForm
                        playerId={data.player.id}
                        mutation={CLOSE_FREE_SPIN_WAGERING_CYCLE_MUTATION}
                        title="Close Free Spin Wagering Cycle"
                        message="Are you sure you want to close the free spin wagering cycle? The all games cycle is not affected."
                        onSuccess={() =>
                          refresh({ requestPolicy: 'network-only' })
                        }
                      />
                    ) : null
                  }
                >
                  <InlineIconButton>
                    <EditIcon />
                  </InlineIconButton>
                </ControlledModal>
              </div>
              <div className="grid gap-2 pt-1">
                <WageringCycleBar
                  fetching={fetching}
                  title="Free spins (slots & crash)"
                  totalWagered={freeSpinCycle.totalWagered}
                  totalDeposited={freeSpinCycle.totalDeposited}
                  remainingToWager={freeSpinCycle.remainingToWager}
                  isMet={freeSpinCycle.isMet}
                  currency={data?.player.wallet?.currency}
                />
              </div>
            </div>
          )}
          {!isEstonia && (
            <div className="col-span-full">
              <label className="text-sm text-gray-500 font-semibold">
                {t(block.depositLimitsLabel)}
              </label>
              <div className="grid gap-2 pt-1">
                <LimitInfo
                  fetching={fetching}
                  limit={data?.player.wallet?.depositLimit?.dayLimit}
                  labelTranslation={block.dailyLabel}
                  currency={data?.player.wallet?.currency}
                  limitPeriodType={LimitTypeEnum.Day}
                  limitTypeTitle={LimitType.Deposit}
                />
                <LimitInfo
                  fetching={fetching}
                  limit={data?.player.wallet?.depositLimit?.weekLimit}
                  labelTranslation={block.weeklyLabel}
                  currency={data?.player.wallet?.currency}
                  limitPeriodType={LimitTypeEnum.Week}
                  limitTypeTitle={LimitType.Deposit}
                />
                <LimitInfo
                  fetching={fetching}
                  limit={data?.player.wallet?.depositLimit?.monthLimit}
                  labelTranslation={block.monthlyLabel}
                  currency={data?.player.wallet?.currency}
                  limitPeriodType={LimitTypeEnum.Month}
                  limitTypeTitle={LimitType.Deposit}
                />
              </div>
            </div>
          )}

          {isEstonia && (
            <div className="col-span-full">
              <label className="text-sm text-gray-500 font-semibold">
                {t(block.lossLimitsLabel)}
              </label>
              <div className="grid gap-2 pt-1">
                <LimitInfo
                  fetching={fetching}
                  limit={data?.player.wallet?.lossLimit?.dayLimit}
                  labelTranslation={block.dailyLabel}
                  currency={data?.player.wallet?.currency}
                  limitPeriodType={LimitTypeEnum.Day}
                  limitTypeTitle={LimitType.Deposit}
                />
                <LimitInfo
                  fetching={fetching}
                  limit={data?.player.wallet?.lossLimit?.weekLimit}
                  labelTranslation={block.weeklyLabel}
                  currency={data?.player.wallet?.currency}
                  limitPeriodType={LimitTypeEnum.Week}
                  limitTypeTitle={LimitType.Deposit}
                />
                <LimitInfo
                  fetching={fetching}
                  limit={data?.player.wallet?.lossLimit?.monthLimit}
                  labelTranslation={block.monthlyLabel}
                  currency={data?.player.wallet?.currency}
                  limitPeriodType={LimitTypeEnum.Month}
                  limitTypeTitle={LimitType.Deposit}
                />
              </div>
            </div>
          )}

          <div className="col-span-full">
            <label className="text-sm text-gray-500 font-semibold">
              {t(block.withdrawalLimitsLabel)}
            </label>
            <div className="grid gap-2 pt-1">
              <LimitInfo
                fetching={fetching}
                limit={data?.player.wallet?.withdrawalLimit?.dayLimit}
                labelTranslation={block.dailyLabel}
                currency={data?.player.wallet?.currency}
                limitPeriodType={LimitTypeEnum.Day}
                limitTypeTitle="Withdrawal"
              />
              <LimitInfo
                fetching={fetching}
                limit={data?.player.wallet?.withdrawalLimit?.monthLimit}
                labelTranslation={block.monthlyLabel}
                currency={data?.player.wallet?.currency}
                limitPeriodType={LimitTypeEnum.Month}
                limitTypeTitle="Withdrawal"
                resetAt={true}
                resetAtTranslation={t(block.resetAtLabel)}
              />
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerWalletBlock;
